import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Button,
  Paper,
  Typography,
  TextField,
  Grid,
  Card,
  CardContent,
  CardActions,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link,
  Switch
} from '@mui/material';
import {
  Check as CheckIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  CreditCard as CreditCardIcon,
  Lock as LockIcon,
  Email as EmailIcon,
  Visibility,
  VisibilityOff,
  CheckCircle,
  Cancel
} from '@mui/icons-material';
import axios from 'axios';
import brandingService from '../../services/branding.service';
import GooglePlacesAutocompleteNative from '../Common/GooglePlacesAutocompleteNative';
import { getApiUrl } from '../../config/runtime';

// Define plan interfaces
interface Plan {
  id: string;
  name: string;
  price: number;
  discountedPrice?: number;
  features: string[];
  recommended?: boolean;
  description: string;
  setupFee: number;
}

interface FormData {
  // Company Info
  companyName: string;
  industry: string;

  // Contact Info
  firstName: string;
  lastName: string;
  email: string;
  phone: string;

  // Address
  address: string;
  unit: string;
  specialInstructions: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;

  // Phone Number Selection
  selectedNumber: string;
  portRequired: boolean;

  // Payment Info
  cardNumber: string;
  cardName: string;
  expiryMonth: string;
  expiryYear: string;
  cvv: string;

  // Billing Address (if different)
  billingAddress?: {
    address: string;
    unit: string;
    city: string;
    state: string;
    zipCode: string;
  };

  agreeTerms: boolean;
  useBillingAddress: boolean;
}

const Signup: React.FC = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [selectedPlan, setSelectedPlan] = useState<string>('professional');
  const [signupSuccess, setSignupSuccess] = useState(false);
  const [customerDetails, setCustomerDetails] = useState<any>(null);
  const [formData, setFormData] = useState<FormData>({
    companyName: '',
    industry: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    unit: '',
    specialInstructions: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'CA',
    selectedNumber: '',
    portRequired: false,
    cardNumber: '',
    cardName: '',
    expiryMonth: '',
    expiryYear: '',
    cvv: '',
    agreeTerms: false,
    useBillingAddress: true
  });

  const [availableNumbers, setAvailableNumbers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchCity, setSearchCity] = useState('');

  // Phone number selection states - matching PhoneNumbers pattern
  const [cities, setCities] = useState<string[]>([]);
  const [areas, setAreas] = useState<any[]>([]);
  const [selectedCity, setSelectedCity] = useState('');
  const [selectedArea, setSelectedArea] = useState('');
  const [searchingNumbers, setSearchingNumbers] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);
  const [loadingAreas, setLoadingAreas] = useState(false);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [branding, setBranding] = useState<any>(null);
  const [featuresDialog, setFeaturesDialog] = useState<{ open: boolean; plan: Plan | null }>({
    open: false,
    plan: null
  });
  const [cardType, setCardType] = useState<'visa' | 'mastercard' | 'amex' | 'unknown' | null>(null);
  const [cardError, setCardError] = useState<string>('');

  // Load branding on mount
  useEffect(() => {
    const loadBranding = async () => {
      try {
        const brandData = await brandingService.getCurrentBranding();
        setBranding(brandData);
      } catch (error) {
        // If branding completely fails to load, we can still proceed with default branding
        console.error('Failed to load branding, using defaults:', error);
        setBranding({
          brand_name: 'Portal',
          logo_url: '/logo.png',
          metadata: {}
        });
      }
    };
    loadBranding();
  }, []);

  // Fetch available plans from the database
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await axios.get('/api/v1/endpoints/packages-public.php');
        if (response.data.success) {
          const fetchedPlans = response.data.data.packages.map((pkg: any) => ({
            id: pkg.id.toString(),
            name: pkg.name,
            price: pkg.price,
            discountedPrice: pkg.discounted_price,
            setupFee: pkg.setup_fee,
            description: pkg.description,
            features: pkg.features,
            recommended: pkg.recommended,
            includedExtensions: pkg.included_extensions,
            includedDids: pkg.included_dids,
            includedMinutes: pkg.included_minutes,
            extraMinuteRate: pkg.extra_minute_rate
          }));

          setPlans(fetchedPlans);

          // Auto-select the recommended plan or the middle one
          const recommendedPlan = fetchedPlans.find((p: Plan) => p.recommended);
          if (recommendedPlan) {
            setSelectedPlan(recommendedPlan.id);
          } else if (fetchedPlans.length > 0) {
            // Select the middle plan if no recommended
            const middleIndex = Math.floor(fetchedPlans.length / 2);
            setSelectedPlan(fetchedPlans[middleIndex].id);
          }
        }
      } catch (err) {
        console.error('Failed to fetch packages:', err);
        setError('Unable to load available packages. Please try again later.');
      } finally {
        setLoadingPlans(false);
      }
    };

    fetchPlans();
  }, []);

  const steps = [
    'Choose Your Plan',
    'Number Porting',
    'Select Phone Number',
    'Company Information',
    'Payment Details',
    'Review & Complete'
  ];

  // Fetch available cities
  const fetchCities = async () => {
    setLoadingCities(true);
    try {
      const response = await axios.get('/api/v1/endpoints/phone-numbers-public.php', {
        params: { action: 'get_cities' }
      });
      const citiesData = response.data.cities || [];

      // Cities are already strings from the new API
      setCities(citiesData);
    } catch (err) {
      console.error('Failed to fetch cities:', err);
      setError('Failed to load cities');
    } finally {
      setLoadingCities(false);
    }
  };

  // Fetch areas for selected city
  const fetchAreas = async (city: string) => {
    setLoadingAreas(true);
    setAreas([]);
    setAvailableNumbers([]);
    try {
      const response = await axios.get('/api/v1/endpoints/phone-numbers-public.php', {
        params: {
          action: 'get_areas',
          city: city
        }
      });
      setAreas(response.data.areas || []);
    } catch (err) {
      console.error('Failed to fetch areas:', err);
      setError('Failed to load areas');
    } finally {
      setLoadingAreas(false);
    }
  };

  // Search for available phone numbers
  const searchPhoneNumbers = async (npa: string, nxx: string) => {
    setSearchingNumbers(true);
    setAvailableNumbers([]);
    try {
      const response = await axios.get('/api/v1/endpoints/phone-numbers-public.php', {
        params: {
          action: 'search_local_numbers',
          npa: npa,
          nxx: nxx,
          limit: 10
        }
      });
      if (response.data.numbers) {
        setAvailableNumbers(response.data.numbers);
      }
    } catch (err) {
      console.error('Failed to fetch phone numbers:', err);
      setError('Failed to search numbers');
    } finally {
      setSearchingNumbers(false);
    }
  };

  useEffect(() => {
    if (activeStep === 2 && cities.length === 0) {
      fetchCities();
    }
  }, [activeStep]);

  const handleNext = () => {
    // Special validation with alert for Customer Information step
    if (activeStep === 3) {
      const errors: string[] = [];

      if (!formData.companyName || formData.companyName.length < 2) {
        errors.push('• Company name must be at least 2 characters');
      }
      if (!formData.firstName || formData.firstName.length < 2) {
        errors.push('• First name must be at least 2 characters');
      }
      if (/\d/.test(formData.firstName)) {
        errors.push('• First name cannot contain numbers');
      }
      if (!formData.lastName || formData.lastName.length < 2) {
        errors.push('• Last name must be at least 2 characters');
      }
      if (/\d/.test(formData.lastName)) {
        errors.push('• Last name cannot contain numbers');
      }
      if (!formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        errors.push('• Please enter a valid email address');
      }
      if (!formData.phone || formData.phone.replace(/\D/g, '').length !== 10) {
        errors.push('• Phone number must be exactly 10 digits');
      }
      if (!formData.address) {
        errors.push('• Street address is required');
      }
      if (!formData.city) {
        errors.push('• City is required');
      }
      if (!formData.state) {
        errors.push('• Province is required');
      }
      if (!formData.zipCode || !/^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/.test(formData.zipCode)) {
        errors.push('• Please enter a valid Canadian postal code (e.g., K1A 0B1)');
      }

      if (errors.length > 0) {
        alert('Please fix the following errors:\n\n' + errors.join('\n'));
        return;
      }
    }

    // Validate current step before proceeding
    if (!validateStep(activeStep)) {
      return;
    }
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const validateStep = (step: number): boolean => {
    switch (step) {
      case 0:
        return !!selectedPlan;
      case 1:
        // Number porting step - always allow to proceed (no validation needed)
        return true;
      case 2:
        return !!formData.selectedNumber;
      case 3:
        // Basic check for button state - detailed validation happens in handleNext
        return !!(
          formData.companyName &&
          formData.firstName &&
          formData.lastName &&
          formData.email &&
          formData.phone &&
          formData.address &&
          formData.city &&
          formData.state &&
          formData.zipCode
        );
      case 4:
        return !!(
          formData.cardNumber &&
          formData.cardName &&
          formData.expiryMonth &&
          formData.expiryYear &&
          formData.cvv &&
          formData.agreeTerms &&
          !cardError &&
          cardType !== 'amex' &&
          (cardType === 'visa' || cardType === 'mastercard')
        );
      default:
        return true;
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);

    try {
      const plan = plans.find(p => p.id === selectedPlan);
      const apiUrl = await getApiUrl();
      const response = await axios.post(`${apiUrl}/endpoints/public-signup.php`, {
        plan: selectedPlan,
        plan_price: plan?.discountedPrice || plan?.price,
        company: formData.companyName,
        industry: formData.industry,
        domain: window.location.hostname, // Include domain for proper branding
        customer: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: formData.phone,
        },
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        address: {
          address: formData.address,
          unit: formData.unit,
          special_instructions: formData.specialInstructions,
          city: formData.city,
          state: formData.state,
          zip_code: formData.zipCode,
          country: formData.country
        },
        phone_number: formData.selectedNumber.replace(/\D/g, ''),
        port_required: formData.portRequired ? 1 : 0,
        payment: {
          card_number: formData.cardNumber,
          card_name: formData.cardName,
          expiry_month: formData.expiryMonth,
          expiry_year: formData.expiryYear,
          cvv: formData.cvv
        }
      });

      if (response.data.status === 'success' || response.data.success) {
        // Show success message instead of redirecting
        setSignupSuccess(true);
        setCustomerDetails(response.data.data || response.data);
      } else {
        setError(response.data.message || 'Signup failed');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'An error occurred during signup');
    }
    setLoading(false);
  };

  const updateFormData = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Detect card type based on BIN (Bank Identification Number)
  const detectCardType = (cardNumber: string): 'visa' | 'mastercard' | 'amex' | 'unknown' => {
    const cleaned = cardNumber.replace(/\s/g, '');

    // Visa: starts with 4
    if (/^4/.test(cleaned)) {
      return 'visa';
    }

    // Mastercard: starts with 51-55 or 2221-2720
    if (/^5[1-5]/.test(cleaned) || /^2(2(2[1-9]|[3-9])|[3-6]|7[0-1]|720)/.test(cleaned)) {
      return 'mastercard';
    }

    // American Express: starts with 34 or 37
    if (/^3[47]/.test(cleaned)) {
      return 'amex';
    }

    return 'unknown';
  };

  // Format card number with spaces
  const formatCardNumber = (value: string): string => {
    const cleaned = value.replace(/\D/g, '');
    const match = cleaned.match(/.{1,4}/g);
    return match ? match.join(' ') : cleaned;
  };

  // Handle card number input change
  const handleCardNumberChange = (value: string) => {
    // Format the card number
    const formatted = formatCardNumber(value);
    updateFormData('cardNumber', formatted);

    // Detect card type
    const cleaned = value.replace(/\s/g, '');
    if (cleaned.length >= 2) {
      const type = detectCardType(cleaned);
      setCardType(type);

      // Check if it's Amex and show error
      if (type === 'amex') {
        setCardError('American Express cards are not accepted. Please use Visa or Mastercard.');
      } else if (type === 'unknown' && cleaned.length >= 6) {
        setCardError('Card type not recognized. We only accept Visa and Mastercard.');
      } else {
        setCardError('');
      }
    } else {
      setCardType(null);
      setCardError('');
    }
  };

  const renderPlanSelection = () => (
    <Grid container spacing={3} sx={{ pt: 2 }}>
      {plans.map((plan) => (
        <Grid item xs={12} md={4} key={plan.id} sx={{ mt: plan.recommended ? 2 : 0 }}>
          <Card
            elevation={selectedPlan === plan.id ? 8 : 2}
            sx={{
              height: '100%',
              position: 'relative',
              border: selectedPlan === plan.id ? '2px solid #1976d2' : 'none',
              borderRadius: 2,
              overflow: 'visible'
            }}
          >
            {plan.recommended && (
              <Box
                sx={{
                  position: 'absolute',
                  top: -16,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  bgcolor: 'warning.main',
                  color: 'white',
                  px: 2,
                  py: 0.5,
                  borderRadius: 1,
                  fontSize: '0.875rem',
                  fontWeight: 'bold',
                  zIndex: 1,
                  whiteSpace: 'nowrap'
                }}
              >
                MOST POPULAR
              </Box>
            )}

            <CardContent sx={{ pb: 1, px: 1.5, pt: 1.5 }}>
              <Typography variant="body1" gutterBottom align="center" fontWeight="bold" sx={{ fontSize: '1.1rem', mb: 0.5 }}>
                {plan.name}
              </Typography>

              <Typography variant="caption" color="text.secondary" align="center" display="block" mb={0.5} sx={{ fontSize: '0.75rem' }}>
                {plan.description}
              </Typography>

              <Box textAlign="center" mb={1}>
                {plan.discountedPrice ? (
                  <>
                    <Typography variant="h5" component="span" color="primary" fontWeight="bold">
                      ${plan.discountedPrice}
                    </Typography>
                    <Typography
                      variant="body1"
                      component="span"
                      color="text.secondary"
                      sx={{ textDecoration: 'line-through', ml: 1 }}
                    >
                      ${plan.price}
                    </Typography>
                    <Typography variant="caption" display="block" color="warning.main">
                      /month for first 12 months
                    </Typography>
                  </>
                ) : (
                  <>
                    <Typography variant="h5" component="span" color="primary" fontWeight="bold">
                      ${plan.price}
                    </Typography>
                    <Typography variant="caption" display="block" color="text.secondary">
                      per month
                    </Typography>
                  </>
                )}
              </Box>

              <Divider sx={{ my: 0.75 }} />

              <List dense sx={{ py: 0 }}>
                {plan.features.slice(0, 5).map((feature, index) => (
                  <ListItem key={index} sx={{ px: 0, py: 0 }}>
                    <ListItemIcon sx={{ minWidth: 20 }}>
                      <CheckIcon color="success" sx={{ fontSize: 14 }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={feature}
                      primaryTypographyProps={{ variant: 'caption', lineHeight: 1.2, fontSize: '0.75rem' }}
                    />
                  </ListItem>
                ))}
                {plan.features.length > 5 && (
                  <ListItem
                    sx={{
                      px: 0,
                      py: 0,
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: 'action.hover'
                      }
                    }}
                    onClick={() => setFeaturesDialog({ open: true, plan })}
                  >
                    <ListItemText
                      primary={
                        <Link
                          component="button"
                          variant="caption"
                          onClick={(e) => {
                            e.stopPropagation();
                            setFeaturesDialog({ open: true, plan });
                          }}
                          sx={{ fontSize: '0.75rem' }}
                        >
                          + {plan.features.length - 5} more features
                        </Link>
                      }
                    />
                  </ListItem>
                )}
              </List>
            </CardContent>

            <CardActions sx={{ justifyContent: 'center', pb: 1.5, pt: 0 }}>
              <Button
                variant={selectedPlan === plan.id ? "contained" : "outlined"}
                onClick={() => setSelectedPlan(plan.id)}
                fullWidth
                size="medium"
              >
                {selectedPlan === plan.id ? 'Selected' : 'Choose this plan'}
              </Button>
            </CardActions>
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  const renderNumberPortingQuestion = () => (
    <Box sx={{ maxWidth: 800, mx: 'auto', textAlign: 'center', py: 4 }}>
      <Typography variant="h4" gutterBottom fontWeight="bold" sx={{ mb: 3 }}>
        Do you want to keep your existing phone number(s)?
      </Typography>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 4, fontSize: '1.1rem' }}>
        If you have existing phone numbers you'd like to transfer to our service, we can help you port them.
      </Typography>

      <Paper sx={{ p: 4, bgcolor: 'grey.50', maxWidth: 600, mx: 'auto' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
            '&:hover': { bgcolor: 'grey.100' },
            p: 2,
            borderRadius: 1,
            transition: 'background-color 0.2s'
          }}
          onClick={() => updateFormData('portRequired', !formData.portRequired)}
        >
          <Box sx={{ textAlign: 'left', flex: 1 }}>
            <Typography variant="h6" fontWeight="medium" sx={{ mb: 0.5, fontSize: '1.3rem' }}>
              I want to port my existing number(s)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1rem' }}>
              Transfer your current phone number(s) to our service
            </Typography>
          </Box>
          <Switch
            checked={formData.portRequired}
            onChange={(e) => {
              e.stopPropagation();
              updateFormData('portRequired', e.target.checked);
            }}
            color="primary"
            sx={{
              '& .MuiSwitch-switchBase.Mui-checked': {
                color: 'primary.main',
              },
              '& .MuiSwitch-track': {
                backgroundColor: formData.portRequired ? 'primary.main' : 'grey.400',
              },
            }}
          />
        </Box>

        {formData.portRequired && (
          <Alert severity="info" sx={{ mt: 3, textAlign: 'left' }}>
            <Typography variant="body2" sx={{ fontSize: '1rem' }}>
              Perfect! On the next step, you'll select a <strong>temporary number</strong> to use while we transfer your existing number(s).
              After signup, we'll send you detailed instructions on how to complete the porting process.
            </Typography>
          </Alert>
        )}
      </Paper>

      <Box sx={{ mt: 4 }}>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.95rem' }}>
          <strong>Note:</strong> You can always port numbers later if you change your mind.
        </Typography>
      </Box>
    </Box>
  );

  const renderPhoneNumberSelection = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        {formData.portRequired ? 'Choose a Temporary Phone Number' : 'Choose Your Business Phone Number'}
      </Typography>

      {formData.portRequired && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            Select a temporary number below. This will be used while we transfer your existing number(s).
            You'll receive porting instructions after completing your signup.
          </Typography>
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* City Selection */}
        <Grid item xs={12} md={4}>
          <FormControl fullWidth size="small">
            <InputLabel>City</InputLabel>
            <Select
              value={selectedCity}
              onChange={async (e) => {
                const city = e.target.value;
                setSelectedCity(city);
                setSelectedArea('');
                setAvailableNumbers([]);
                if (city) {
                  await fetchAreas(city);
                }
              }}
              label="City"
              disabled={loadingCities}
            >
              <MenuItem value="">Select a city</MenuItem>
              {cities.map((city) => (
                <MenuItem key={city} value={city}>
                  {city}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Area Selection */}
        <Grid item xs={12} md={4}>
          <FormControl fullWidth size="small" disabled={!selectedCity || loadingAreas}>
            <InputLabel>Area</InputLabel>
            <Select
              value={selectedArea}
              onChange={(e) => {
                const areaValue = e.target.value;
                setSelectedArea(areaValue);

                // Find the selected area object to get npa and nxx
                const selectedAreaObj = areas.find((area: any) =>
                  `${area.name} (${area.npa}-${area.nxx})` === areaValue
                );

                if (selectedAreaObj) {
                  searchPhoneNumbers(selectedAreaObj.npa, selectedAreaObj.nxx);
                }
              }}
              label="Area"
            >
              <MenuItem value="">Select an area</MenuItem>
              {areas.map((area: any) => (
                <MenuItem
                  key={`${area.npa}-${area.nxx}`}
                  value={`${area.name} (${area.npa}-${area.nxx})`}
                >
                  {area.name} ({area.npa}-{area.nxx})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Search Status */}
        <Grid item xs={12} md={4}>
          {searchingNumbers && (
            <Box display="flex" alignItems="center" height="40px">
              <CircularProgress size={20} sx={{ mr: 1 }} />
              <Typography variant="body2">Searching for numbers...</Typography>
            </Box>
          )}
          {!searchingNumbers && availableNumbers.length > 0 && (
            <Typography variant="body2" color="success.main">
              {availableNumbers.length} numbers available
            </Typography>
          )}
        </Grid>
      </Grid>

      {/* Available Numbers List */}
      {availableNumbers.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Available Numbers
          </Typography>
          <RadioGroup
            value={formData.selectedNumber}
            onChange={(e) => updateFormData('selectedNumber', e.target.value)}
          >
            <Grid container spacing={2}>
              {availableNumbers.map((number) => {
                // Handle both string format and object format
                const displayNumber = typeof number === 'string' ? number : number.number;
                const formattedNumber = typeof number === 'string'
                  ? number
                  : `(${number.npa}) ${number.nxx}-${number.number.slice(-4)}`;

                return (
                  <Grid item xs={12} sm={6} md={4} key={displayNumber}>
                    <Paper
                      sx={{
                        p: 2,
                        cursor: 'pointer',
                        border: formData.selectedNumber === displayNumber ? '2px solid #1976d2' : '1px solid #e0e0e0',
                        '&:hover': { borderColor: '#1976d2' },
                        transition: 'border-color 0.2s'
                      }}
                      onClick={() => updateFormData('selectedNumber', displayNumber)}
                    >
                      <FormControlLabel
                        value={displayNumber}
                        control={<Radio checked={formData.selectedNumber === displayNumber} />}
                        label={
                          <Box display="flex" alignItems="center">
                            <PhoneIcon sx={{ mr: 1 }} />
                            <Typography variant="h6">{formattedNumber}</Typography>
                          </Box>
                        }
                      />
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          </RadioGroup>
        </Box>
      )}

      {/* No selection message */}
      {!selectedCity && !loadingCities && (
        <Box sx={{ mt: 3, p: 3, textAlign: 'center', bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="body1" color="text.secondary">
            Select a city to see available phone numbers
          </Typography>
        </Box>
      )}

      {selectedCity && !selectedArea && !loadingAreas && (
        <Box sx={{ mt: 3, p: 3, textAlign: 'center', bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="body1" color="text.secondary">
            Select an area to see available phone numbers
          </Typography>
        </Box>
      )}

      {selectedArea && !searchingNumbers && availableNumbers.length === 0 && (
        <Box sx={{ mt: 3, p: 3, textAlign: 'center', bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="body1" color="text.secondary">
            No numbers available in this area. Please try another area.
          </Typography>
        </Box>
      )}
    </Box>
  );

  const renderCompanyInfo = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Company Information
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <TextField
            required
            fullWidth
            label="Company Name"
            value={formData.companyName}
            onChange={(e) => updateFormData('companyName', e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <BusinessIcon />
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Industry</InputLabel>
            <Select
              value={formData.industry}
              onChange={(e) => updateFormData('industry', e.target.value)}
              label="Industry"
            >
              <MenuItem value="healthcare">Healthcare</MenuItem>
              <MenuItem value="legal">Legal</MenuItem>
              <MenuItem value="finance">Finance</MenuItem>
              <MenuItem value="retail">Retail</MenuItem>
              <MenuItem value="technology">Technology</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            required
            fullWidth
            label="First Name"
            value={formData.firstName}
            onChange={(e) => updateFormData('firstName', e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonIcon />
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            required
            fullWidth
            label="Last Name"
            value={formData.lastName}
            onChange={(e) => updateFormData('lastName', e.target.value)}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            required
            fullWidth
            type="email"
            label="Email Address"
            value={formData.email}
            onChange={(e) => updateFormData('email', e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailIcon />
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            required
            fullWidth
            label="Phone Number"
            value={formData.phone}
            onChange={(e) => updateFormData('phone', e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PhoneIcon />
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" gutterBottom fontWeight="bold">
            Search for and select your business address
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <GooglePlacesAutocompleteNative
            value={formData.address}
            onChange={(value: string) => updateFormData('address', value)}
            onPlaceSelected={(place: any) => {
              // Parse the address components from Google Places
              let streetNumber = '';
              let streetName = '';
              let city = '';
              let state = '';
              let zipCode = '';
              let country = 'US';

              if (place.address_components) {
                place.address_components.forEach((component: any) => {
                  const types = component.types;

                  if (types.includes('street_number')) {
                    streetNumber = component.long_name;
                  }
                  if (types.includes('route')) {
                    streetName = component.long_name;
                  }
                  if (types.includes('locality')) {
                    city = component.long_name;
                  }
                  if (types.includes('administrative_area_level_1')) {
                    state = component.short_name;
                  }
                  if (types.includes('postal_code')) {
                    zipCode = component.long_name;
                  }
                  if (types.includes('country')) {
                    country = component.short_name;
                  }
                });

                // Update form data with parsed address
                const fullAddress = streetNumber ? `${streetNumber} ${streetName}` : streetName;
                updateFormData('address', fullAddress);
                updateFormData('city', city);
                updateFormData('state', state);
                updateFormData('zipCode', zipCode);
                updateFormData('country', country);
              }
            }}
            label=""
            placeholder="Start typing an address..."
            helperText=""
          />
        </Grid>

        {formData.address && (
          <>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Unit/Suite (Optional)"
                value={formData.unit}
                onChange={(e) => updateFormData('unit', e.target.value)}
                helperText="Add unit/suite if applicable"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Special Instructions"
                value={formData.specialInstructions}
                onChange={(e) => updateFormData('specialInstructions', e.target.value)}
                placeholder="e.g., buzzer code, back door, 2nd floor"
                helperText="Special instructions for delivery, access, etc."
              />
            </Grid>
          </>
        )}

        <Grid item xs={12}>
          <Alert severity="info" sx={{ mt: 2 }}>
            After completing signup, you'll receive an email with a secure link to create your password.
          </Alert>
        </Grid>
      </Grid>
    </Box>
  );

  const renderPaymentDetails = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Payment Information
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        Your card will be charged immediately upon signup. You can cancel anytime.
      </Alert>

      <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3 }}>
          <Typography variant="subtitle2" fontWeight="bold" color="text.secondary">
            We Accept:
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Box
              component="img"
              src="https://upload.wikimedia.org/wikipedia/commons/0/04/Visa.svg"
              alt="Visa"
              sx={{ height: 30 }}
            />
            <Box
              component="img"
              src="https://upload.wikimedia.org/wikipedia/commons/2/2a/Mastercard-logo.svg"
              alt="Mastercard"
              sx={{ height: 30 }}
            />
          </Box>
        </Box>
      </Paper>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <TextField
            required
            fullWidth
            label="Cardholder Name"
            value={formData.cardName}
            onChange={(e) => updateFormData('cardName', e.target.value)}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            required
            fullWidth
            label="Card Number"
            value={formData.cardNumber}
            onChange={(e) => handleCardNumberChange(e.target.value)}
            placeholder="1234 5678 9012 3456"
            error={!!cardError}
            helperText={cardError || (cardType && cardType !== 'unknown' && cardType !== 'amex' ? `${cardType === 'visa' ? 'Visa' : 'Mastercard'} card detected` : '')}
            inputProps={{ maxLength: 19 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <CreditCardIcon color={cardType === 'visa' || cardType === 'mastercard' ? 'success' : cardType === 'amex' ? 'error' : 'action'} />
                </InputAdornment>
              ),
              endAdornment: cardType && (
                <InputAdornment position="end">
                  {cardType === 'visa' && <CheckCircle color="success" />}
                  {cardType === 'mastercard' && <CheckCircle color="success" />}
                  {cardType === 'amex' && <Cancel color="error" />}
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid item xs={6}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <FormControl fullWidth required>
                <InputLabel>Month</InputLabel>
                <Select
                  value={formData.expiryMonth}
                  onChange={(e) => updateFormData('expiryMonth', e.target.value)}
                  label="Month"
                >
                  {[...Array(12)].map((_, i) => (
                    <MenuItem key={i + 1} value={String(i + 1).padStart(2, '0')}>
                      {String(i + 1).padStart(2, '0')}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth required>
                <InputLabel>Year</InputLabel>
                <Select
                  value={formData.expiryYear}
                  onChange={(e) => updateFormData('expiryYear', e.target.value)}
                  label="Year"
                >
                  {[...Array(10)].map((_, i) => {
                    const year = new Date().getFullYear() + i;
                    return (
                      <MenuItem key={year} value={String(year)}>
                        {year}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={6}>
          <TextField
            required
            fullWidth
            label="CVV"
            value={formData.cvv}
            onChange={(e) => updateFormData('cvv', e.target.value)}
            placeholder="123"
            inputProps={{ maxLength: 4 }}
          />
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.useBillingAddress}
                onChange={(e) => updateFormData('useBillingAddress', e.target.checked)}
              />
            }
            label="Billing address same as service address"
          />
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.agreeTerms}
                onChange={(e) => updateFormData('agreeTerms', e.target.checked)}
                color="primary"
              />
            }
            label="I agree to the Terms of Service and Privacy Policy"
          />
        </Grid>
      </Grid>
    </Box>
  );

  const renderReview = () => {
    const plan = plans.find(p => p.id === selectedPlan);

    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          Review Your Order
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Plan Details
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="h5" color="primary">
                  {plan?.name} Plan
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {plan?.description}
                </Typography>
              </Box>
              <Divider sx={{ my: 2 }} />
              <Box display="flex" justifyContent="space-between">
                <Typography>Monthly Fee:</Typography>
                <Typography fontWeight="bold">
                  ${plan?.discountedPrice || plan?.price}/mo
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography>Setup Fee:</Typography>
                <Typography fontWeight="bold">
                  ${plan?.setupFee}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography>Selected Number:</Typography>
                <Typography fontWeight="bold">
                  {formData.selectedNumber}
                </Typography>
              </Box>
              <Divider sx={{ my: 2 }} />
              {(() => {
                const monthlyFee = Number(plan?.discountedPrice || plan?.price || 0);
                const setupFee = Number(plan?.setupFee || 0);
                const subtotal = monthlyFee + setupFee;

                // Tax rates by province
                const taxRates: { [key: string]: number } = {
                  'ON': 0.13, 'BC': 0.12, 'AB': 0.05, 'SK': 0.11, 'MB': 0.12,
                  'QC': 0.14975, 'NB': 0.15, 'NS': 0.15, 'PE': 0.15, 'NL': 0.15,
                  'YT': 0.05, 'NT': 0.05, 'NU': 0.05
                };

                const taxRate = taxRates[formData.state] || 0.13; // Default to 13% if unknown
                const taxAmount = subtotal * taxRate;

                // Convenience fee: 2.4% for all provinces except Quebec (0%)
                const convenienceFeeRate = formData.state === 'QC' ? 0 : 0.024;
                const convenienceFee = subtotal * convenienceFeeRate;

                const total = subtotal + taxAmount + convenienceFee;

                return (
                  <>
                    <Box display="flex" justifyContent="space-between">
                      <Typography>Subtotal:</Typography>
                      <Typography>${subtotal.toFixed(2)}</Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <Typography>Tax ({formData.state} - {(taxRate * 100).toFixed(2)}%):</Typography>
                      <Typography>${taxAmount.toFixed(2)}</Typography>
                    </Box>
                    {convenienceFee > 0 && (
                      <Box display="flex" justifyContent="space-between">
                        <Typography>Convenience Fee (2.4%):</Typography>
                        <Typography>${convenienceFee.toFixed(2)}</Typography>
                      </Box>
                    )}
                    {formData.state === 'QC' && (
                      <Box display="flex" justifyContent="space-between">
                        <Typography color="success.main">Quebec - No Convenience Fee:</Typography>
                        <Typography color="success.main">$0.00</Typography>
                      </Box>
                    )}
                    <Divider sx={{ my: 1 }} />
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="h6" fontWeight="bold">Total Due Today:</Typography>
                      <Typography variant="h6" fontWeight="bold" color="primary">
                        ${total.toFixed(2)}
                      </Typography>
                    </Box>
                  </>
                );
              })()}
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Account Information
              </Typography>
              <Box sx={{ mb: 1 }}>
                <Typography variant="body2" color="text.secondary">Company</Typography>
                <Typography>{formData.companyName}</Typography>
              </Box>
              <Box sx={{ mb: 1 }}>
                <Typography variant="body2" color="text.secondary">Contact</Typography>
                <Typography>{formData.firstName} {formData.lastName}</Typography>
              </Box>
              <Box sx={{ mb: 1 }}>
                <Typography variant="body2" color="text.secondary">Email</Typography>
                <Typography>{formData.email}</Typography>
              </Box>
              <Box sx={{ mb: 1 }}>
                <Typography variant="body2" color="text.secondary">Phone</Typography>
                <Typography>{formData.phone}</Typography>
              </Box>
              <Box sx={{ mb: 1 }}>
                <Typography variant="body2" color="text.secondary">Service Address</Typography>
                <Typography>
                  {formData.address} {formData.unit}<br />
                  {formData.city}, {formData.state} {formData.zipCode}
                </Typography>
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {error && (
          <Alert severity="error" sx={{ mt: 3 }}>
            {error}
          </Alert>
        )}
      </Box>
    );
  };

  const getStepContent = (step: number) => {
    switch (step) {
      case 0:
        return renderPlanSelection();
      case 1:
        return renderNumberPortingQuestion();
      case 2:
        return renderPhoneNumberSelection();
      case 3:
        return renderCompanyInfo();
      case 4:
        return renderPaymentDetails();
      case 5:
        return renderReview();
      default:
        return 'Unknown step';
    }
  };

  // Success screen component
  const renderSuccess = () => (
    <Box textAlign="center" py={4}>
      <CheckCircle color="success" sx={{ fontSize: 80, mb: 3 }} />

      <Typography variant="h4" gutterBottom fontWeight="bold" color="success.main">
        Account Created Successfully!
      </Typography>

      <Typography variant="h6" gutterBottom color="text.secondary" sx={{ mb: 4 }}>
        Please check your email to verify your account
      </Typography>

      <Paper sx={{ p: 4, mb: 4, maxWidth: 600, mx: 'auto', bgcolor: '#f0f8ff' }}>
        <Typography variant="body1" paragraph>
          We've sent a verification email to:
        </Typography>
        <Typography variant="h6" fontWeight="bold" paragraph>
          {formData.email}
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Please click the link in the email to activate your account. The link will expire in 24 hours.
        </Typography>
      </Paper>

      {customerDetails && (
        <Paper sx={{ p: 3, maxWidth: 600, mx: 'auto', mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Your Account Details
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">Company</Typography>
              <Typography variant="body1" fontWeight="bold">
                {customerDetails.CompanyName || formData.companyName}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">Customer ID</Typography>
              <Typography variant="body1" fontWeight="bold">
                {customerDetails.CustID}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">Phone Number</Typography>
              <Typography variant="body1" fontWeight="bold">
                {customerDetails.DID || formData.selectedNumber}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">Extension</Typography>
              <Typography variant="body1" fontWeight="bold">
                1000
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      )}

      <Box>
        <Typography variant="body2" color="text.secondary" paragraph>
          Didn't receive the email? Check your spam folder or
        </Typography>
        <Button
          variant="outlined"
          onClick={() => {
            // Could implement resend functionality here
            alert('Resend functionality would be implemented here');
          }}
          sx={{ mt: 1 }}
        >
          Resend Verification Email
        </Button>
      </Box>

      <Box mt={4}>
        <Typography variant="body2" color="text.secondary">
          After verifying your email, you can log in at:
        </Typography>
        <Button
          variant="text"
          href="/login"
          sx={{ mt: 1 }}
        >
          Go to Login Page
        </Button>
      </Box>
    </Box>
  );

  // Show success screen if signup was successful
  if (signupSuccess) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', py: 4 }}>
        <Container maxWidth="lg">
          <Paper elevation={3} sx={{ p: 4 }}>
            {renderSuccess()}
          </Paper>
        </Container>
      </Box>
    );
  }

  // Show loading while branding is being fetched
  if (!branding) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      {/* Features Dialog */}
      <Dialog
        open={featuresDialog.open}
        onClose={() => setFeaturesDialog({ open: false, plan: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {featuresDialog.plan?.name} - All Features
        </DialogTitle>
        <DialogContent>
          <List>
            {featuresDialog.plan?.features.map((feature, index) => (
              <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <CheckIcon color="success" />
                </ListItemIcon>
                <ListItemText primary={feature} />
              </ListItem>
            ))}
          </List>
          {featuresDialog.plan && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="body2" gutterBottom>
                <strong>Price:</strong> ${featuresDialog.plan.discountedPrice || featuresDialog.plan.price}/month
              </Typography>
              {featuresDialog.plan.setupFee > 0 && (
                <Typography variant="body2" gutterBottom>
                  <strong>Setup Fee:</strong> ${featuresDialog.plan.setupFee}
                </Typography>
              )}
              <Typography variant="body2">
                <strong>Description:</strong> {featuresDialog.plan.description}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFeaturesDialog({ open: false, plan: null })}>
            Close
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              if (featuresDialog.plan) {
                setSelectedPlan(featuresDialog.plan.id);
                setFeaturesDialog({ open: false, plan: null });
              }
            }}
          >
            Select This Plan
          </Button>
        </DialogActions>
      </Dialog>

      <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', display: 'flex', flexDirection: 'column' }}>
      {/* Header with Logo */}
      <Box
        component="header"
        sx={{
          bgcolor: 'white',
          borderBottom: '1px solid #e0e0e0',
          py: 2,
          px: 3,
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
        }}
      >
        <Container maxWidth="lg">
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={2}>
              <img
                src={branding.logo_url}
                alt={branding.brand_name}
                style={{ height: '40px', objectFit: 'contain' }}
              />
              <Typography variant="h6" color="primary" fontWeight="500">
                Business Phone System
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Questions? Call us at{' '}
                <Typography component="span" color="primary" fontWeight="bold">
                  {branding.metadata?.support_phone || 'Contact Support'}
                </Typography>
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Main Content */}
      <Box component="main" sx={{ flex: 1, py: 3 }}>
        <Container maxWidth="lg">
          <Paper elevation={1} sx={{ p: 3 }}>
            <Typography variant="h5" align="center" gutterBottom fontWeight="600">
              Get Started with Your Business Phone System
            </Typography>
            <Typography variant="body2" align="center" color="text.secondary" sx={{ mb: 3 }}>
              Join thousands of businesses already using our cloud PBX solution
            </Typography>

          <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          <Box sx={{ mt: 4 }}>
            {getStepContent(activeStep)}
          </Box>

          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between' }}>
            <Button
              disabled={activeStep === 0}
              onClick={handleBack}
              size="large"
            >
              Back
            </Button>

            {activeStep === steps.length - 1 ? (
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                disabled={loading || !formData.agreeTerms}
                size="large"
                startIcon={loading ? <CircularProgress size={20} /> : <CheckIcon />}
              >
                {loading ? 'Processing...' : 'Complete Signup'}
              </Button>
            ) : (
              <Button
                variant="contained"
                color="primary"
                onClick={handleNext}
                disabled={!validateStep(activeStep)}
                size="large"
              >
                Next
              </Button>
            )}
          </Box>
        </Paper>
      </Container>
    </Box>

    {/* Footer */}
    <Box
      component="footer"
      sx={{
        bgcolor: '#2c3e50',
        color: 'white',
        py: 4,
        px: 3,
        mt: 'auto'
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          <Grid item xs={12} md={4}>
            <Typography variant="h6" gutterBottom>
              About {branding.brand_name}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              {branding.metadata?.about_text ||
               'Leading provider of cloud-based business phone systems. Trusted by businesses across Canada.'}
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="h6" gutterBottom>
              Support
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              {branding.metadata?.support_email && `Email: ${branding.metadata.support_email}`}<br />
              {branding.metadata?.support_phone && `Phone: ${branding.metadata.support_phone}`}<br />
              {branding.metadata?.support_hours || 'Monday-Friday 9AM-6PM EST'}
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="h6" gutterBottom>
              Legal
            </Typography>
            <Box sx={{ opacity: 0.8 }}>
              <Typography variant="body2" component="a" href="/terms" sx={{ color: 'inherit', textDecoration: 'none' }}>
                Terms of Service
              </Typography><br />
              <Typography variant="body2" component="a" href="/privacy" sx={{ color: 'inherit', textDecoration: 'none' }}>
                Privacy Policy
              </Typography><br />
              <Typography variant="body2" component="a" href="/acceptable-use" sx={{ color: 'inherit', textDecoration: 'none' }}>
                Acceptable Use Policy
              </Typography>
            </Box>
          </Grid>
        </Grid>
        <Divider sx={{ my: 3, borderColor: 'rgba(255,255,255,0.2)' }} />
        <Typography variant="body2" align="center" sx={{ opacity: 0.6 }}>
          © {new Date().getFullYear()} {branding.metadata?.company_name || branding.brand_name}. All rights reserved.
        </Typography>
      </Container>
    </Box>
  </Box>
    </>
  );
};

export default Signup;